import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Form, Row, Space, Switch, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { Users } from 'lucide-react';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '@stores/auth.store';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useStaffDirectory } from '@hooks/tenant/useStaffDirectory';
import { useModuleLimit } from '@hooks/tenant/useModuleLimit';
import { useCreateStaffMember } from '@hooks/tenant/useCreateStaffMember';
import { useUpdateStaff } from '@hooks/tenant/useUpdateStaff';
import { useDirectoryStatusChangeFlow } from '@hooks/tenant/useDirectoryStatusChangeFlow';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { fetchTenantStaffPage } from '@services/registry';
import { exportToExcel, exportToPdf } from '@/utils/reportExporter';
import { DashboardContainer, KPICardsGrid, StudentsHeaderActions, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from './style';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppPasswordInput } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { Button } from '@components/ui/Button';
import styled, { createGlobalStyle } from 'styled-components';

const { Text } = Typography;

const StaffRegisterModalStyles = createGlobalStyle`
  .staff-register-modal .ant-modal-content {
    max-height: 96vh;
    display: flex;
    flex-direction: column;
  }

  .staff-register-modal .ant-modal-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: inherit;
  }

  .staff-register-modal .ant-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: inherit;
    margin-top: auto;
  }

  .staff-register-modal .ant-modal-body {
    flex: 1;
    overflow: auto;
    max-height: calc(96vh - 160px);
    padding: 16px;
    padding-right: 10px;
    background: inherit;
  }

  .staff-register-modal .ant-modal-body {
    scrollbar-width: thin;
    scrollbar-color: rgba(138, 152, 167, 0.55) transparent;
  }

  .staff-register-modal .ant-modal-body::-webkit-scrollbar {
    width: 8px;
  }

  .staff-register-modal .ant-modal-body::-webkit-scrollbar-track {
    background: transparent;
  }

  .staff-register-modal .ant-modal-body::-webkit-scrollbar-thumb {
    background: rgba(138, 152, 167, 0.45);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .staff-register-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 152, 167, 0.65);
    border: 2px solid transparent;
    background-clip: content-box;
  }
`;

const StyledDatePicker = styled(DatePicker)`
    width: 100%;
    &.ant-picker {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#ffffff')};
        border: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#434343' : '#d9d9d9')};
        border-radius: 12px;
        height: 44px;
        padding: 0 12px;
        font-family: var(--font-family-lufga);
        display: flex;
        align-items: center;
        box-shadow: none;
    }
    &.ant-picker:hover {
        border-color: #8a98a7;
    }
    &.ant-picker-focused {
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 2px rgba(240, 74, 74, 0.15);
    }
    .ant-picker-input > input {
        font-family: var(--font-family-lufga);
        height: 42px;
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#ffffff' : 'rgba(0, 0, 0, 0.88)')};
    }
`;

const ProfileImageRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const ProfileImagePreview = styled.div`
    width: 84px;
    height: 84px;
    border-radius: 14px;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2a2a2a' : '#fafafa')};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const UploadHelp = styled.div`
    margin-top: 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

const FormSectionCard = styled.div`
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 16px;
    padding: 14px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
`;

const IconWrap = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(240, 74, 74, 0.12);
    color: ${({ theme }) => theme.colors.primary};
`;

const SectionTitle = styled.div`
    font-family: var(--font-family-lufga);
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionDivider = styled.div`
    height: 1px;
    background: ${({ theme }) => theme.colors.border.default};
    margin: 12px 0;
`;

function toDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

function toDayjs(value) {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (typeof value?.toDate === 'function') {
        const out = dayjs(value.toDate());
        return out.isValid() ? out : null;
    }
    const out = dayjs(value instanceof Date ? value : value);
    return out.isValid() ? out : null;
}

export default function Staff() {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const { dateFormat, dateInputFormats } = useTenantDateFormat();
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const { rows, total, loading } = useStaffDirectory({ tenantId, page: currentPage, pageSize: currentPageSize });
    const { exceeded: limitExceeded } = useModuleLimit({ moduleKey: 'staff', currentCount: total });
    const createStaffMutation = useCreateStaffMember({ tenantId });
    const updateStaffMutation = useUpdateStaff({ tenantId });
    const { requestStatusChange, StatusChangeModal } = useDirectoryStatusChangeFlow();
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    const [search, setSearch] = useState('');
    const user = useAuthStore((s) => s.user);
    const showDownload = useMemo(() => {
        if (!user) return false;
        if (
            user.role === 'tenant' ||
            user.role === 'owner' ||
            user.role === 'admin' ||
            user.staffRole === 'admin' ||
            user.permissions?.includes('*')
        ) return true;
        return user.permissions?.includes('staffReport.view') || user.permissions?.includes('staffReport.full');
    }, [user]);

    const handleFetchAllStaff = async (onProgress) => {
        let allRows = [];
        let cursor = null;
        let hasMore = true;
        const pageSize = 50;
        let currentLoaded = 0;

        while (hasMore) {
            const res = await fetchTenantStaffPage({
                tenantId,
                pageSize,
                cursor,
            });
            allRows = [...allRows, ...res.rows];
            currentLoaded += res.rows.length;

            const totalEst = search ? filteredRows.length : (res.total || kpis.total || 100);
            onProgress(Math.min(currentLoaded, totalEst), totalEst);

            if (res.rows.length < pageSize || !res.nextCursor) {
                hasMore = false;
            } else {
                cursor = res.nextCursor;
            }
        }

        if (search) {
            const queryClean = search.toLowerCase().trim();
            allRows = allRows.filter((row) => {
                const displayName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
                const email = (row.email || '').toLowerCase();
                const staffId = (row.staffId || '').toLowerCase();
                const roleName = (row.role || '').toLowerCase();
                return (
                    displayName.includes(queryClean) ||
                    email.includes(queryClean) ||
                    staffId.includes(queryClean) ||
                    roleName.includes(queryClean)
                );
            });
        }

        return allRows;
    };

    const onExportExcel = async (onProgress) => {
        const data = await handleFetchAllStaff(onProgress);
        exportToExcel(
            data,
            [
                { title: 'Staff ID', dataIndex: 'staffId' },
                { title: 'First Name', dataIndex: 'firstName' },
                { title: 'Last Name', dataIndex: 'lastName' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Phone', dataIndex: 'phone' },
                { title: 'Role', dataIndex: 'role' },
                { title: 'Status', dataIndex: 'status' },
            ],
            `staff_${tenantId}.xlsx`
        );
    };

    const onExportPdf = async (onProgress) => {
        const data = await handleFetchAllStaff(onProgress);
        exportToPdf(
            data,
            [
                { title: 'Staff ID', dataIndex: 'staffId' },
                { title: 'First Name', dataIndex: 'firstName' },
                { title: 'Last Name', dataIndex: 'lastName' },
                { title: 'Email', dataIndex: 'email' },
                { title: 'Phone', dataIndex: 'phone' },
                { title: 'Role', dataIndex: 'role' },
                { title: 'Status', dataIndex: 'status' },
            ],
            'Staff Report',
            `staff_${tenantId}.pdf`
        );
    };
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create'); // create | edit
    const [editingStaff, setEditingStaff] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [form] = Form.useForm();

    const filteredRows = useMemo(() => {
        const q = (search || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const hay = [
                r.staffId,
                r.firstName,
                r.lastName,
                r.email,
                r.phone,
                r.role,
                r?.currentPosition?.department,
                r?.currentPosition?.designation,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [rows, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const kpis = useMemo(() => {
        const total = rows.length;
        const active = rows.filter((r) => (r?.currentPosition?.status || r.status || 'active') === 'active').length;
        const inactive = total - active;
        return { total, active, inactive };
    }, [rows]);

    const staffDisplayName = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Staff member';
    const isStaffActive = (r) => (r?.currentPosition?.status || r.status || 'active') !== 'inactive';

    const handleStaffStatusSwitch = useCallback(async (row, nextActive) => {
        const cur = isStaffActive(row);
        if (nextActive === cur) return;
        const res = await requestStatusChange({
            displayName: staffDisplayName(row),
            nextActive,
        });
        if (!res.ok) return;
        const staffId = row.id || row.uid;
        if (!staffId) {
            message.error('Missing staff id');
            return;
        }
        setUpdatingStatusId(staffId);
        try {
            await updateStaffMutation.mutateAsync({
                staffId,
                patch: {
                    status: nextActive ? 'active' : 'inactive',
                    currentPosition: {
                        ...(row.currentPosition || {}),
                        status: nextActive ? 'active' : 'inactive',
                        inactiveReason: nextActive ? null : res.reason,
                    },
                },
            });
            message.success(nextActive ? 'Staff activated' : 'Staff deactivated');
        } catch (e) {
            message.error(e?.message || 'Failed to update status');
        } finally {
            setUpdatingStatusId(null);
        }
    }, [requestStatusChange, updateStaffMutation]);

    const columns = useMemo(
        () => [
            { title: 'Staff ID', dataIndex: 'staffId', key: 'staffId' },
            {
                title: 'Name',
                key: 'name',
                render: (_, r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—',
            },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Phone', dataIndex: 'phone', key: 'phone' },
            {
                title: 'Role',
                key: 'role',
                render: (_, r) => r.role ? <Tag color="blue">{r.role}</Tag> : '—',
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, row) => {
                    const active = isStaffActive(row);
                    const reason = row.currentPosition?.inactiveReason || row.inactiveReason;
                    const tip = !active && reason ? reason : active ? 'Active' : 'Inactive';
                    const sid = row.id || row.uid;
                    return (
                        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                            <Tooltip title={tip}>
                                <Switch
                                    checked={active}
                                    loading={updatingStatusId === sid}
                                    onChange={(checked) => void handleStaffStatusSwitch(row, checked)}
                                />
                            </Tooltip>
                        </span>
                    );
                },
            },
        ],
        [handleStaffStatusSwitch, updatingStatusId]
    );

    const closeAndReset = () => {
        setOpen(false);
        setMode('create');
        setEditingStaff(null);
        form.resetFields();
        setProfileImagePreview(null);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const { email, password, confirmPassword, ...profile } = values;

            if (mode === 'create') {
                if (password !== confirmPassword) {
                    message.error('Passwords do not match');
                    return;
                }

                await createStaffMutation.mutateAsync({
                    email,
                    password,
                    profile: {
                        ...profile,
                        profileImage: profileImagePreview || null,
                        currentPosition: {
                            salary: values?.salary ?? profile?.currentPosition?.salary ?? null,
                            status: 'active',
                        },
                    },
                });
                message.success('Staff member created');
            } else {
                const staffId = editingStaff?.id || editingStaff?.uid;
                if (!staffId) throw new Error('Missing staff id');
                await updateStaffMutation.mutateAsync({
                    staffId,
                    patch: {
                        ...profile,
                        profileImage: profileImagePreview ?? editingStaff?.profileImage ?? null,
                        currentPosition: {
                            ...(editingStaff?.currentPosition || {}),
                            ...(profile?.currentPosition || {}),
                            salary: values?.salary ?? profile?.currentPosition?.salary ?? editingStaff?.currentPosition?.salary ?? null,
                        },
                    },
                });
                message.success('Staff updated');
            }
            closeAndReset();
        } catch (e) {
            const msg = e?.message || (mode === 'create' ? 'Failed to create staff member' : 'Failed to update staff member');
            if (msg !== 'Validation failed') message.error(msg);
        }
    };

    const openEdit = (row) => {
        setMode('edit');
        setEditingStaff(row);
        setProfileImagePreview(row?.profileImage || null);
        const existingAuthorities = row?.authorities || {};
        const legacyModules = Array.isArray(existingAuthorities?.modules) ? existingAuthorities.modules : [];
        const existingAccess = existingAuthorities?.access && typeof existingAuthorities.access === 'object'
            ? existingAuthorities.access
            : {};
        const accessDefaults = {
            students: legacyModules.includes('students') ? 'full' : 'no_access',
            teachers: legacyModules.includes('teachers') ? 'full' : 'no_access',
            staff: legacyModules.includes('staff') ? 'full' : 'no_access',
            tenantProfile: legacyModules.includes('tenant_profile') ? 'full' : 'no_access',
            schoolTime: legacyModules.includes('school_time') ? 'full' : 'no_access',
            preferences: legacyModules.includes('preferences') ? 'full' : 'no_access',
            studentAttendance: legacyModules.includes('student_attendance') ? 'full' : 'no_access',
            teacherAttendance: legacyModules.includes('teacher_attendance') ? 'full' : 'no_access',
            staffAttendance: legacyModules.includes('staff_attendance') ? 'full' : 'no_access',
            financeAccounts: 'no_access',
            receiveLedger: 'no_access',
            expenseLedger: 'no_access',
            transactionLedger: 'view',
            feesManagement: 'no_access',
            teacherReport: 'no_access',
            studentReport: 'no_access',
            staffReport: 'no_access',
        };
        form.setFieldsValue({
            ...row,
            dateOfBirth: toDayjs(row?.dateOfBirth),
            currentPosition: {
                ...(row?.currentPosition || {}),
                joiningDate: toDayjs(row?.currentPosition?.joiningDate),
            },
            authorities: {
                ...existingAuthorities,
                access: { ...accessDefaults, ...existingAccess },
            },
        });
        setOpen(true);
    };

    return (
        <DashboardContainer>
            {StatusChangeModal}
            <StaffRegisterModalStyles />

            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Staff</StudentsHeaderTitle>
                    <StudentsHeaderSub>Manage staff members and permissions</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <StudentsHeaderActions>
                    <Tooltip title={limitExceeded ? "Limit is exceeded. Please contact EDULINK to change your Subscription Plan." : ""}>
                        <Button type="primary" disabled={limitExceeded} onClick={() => setOpen(true)}>
                            Create Staff
                        </Button>
                    </Tooltip>
                </StudentsHeaderActions>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={3} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Total Staff" value={kpis.total} />
                <KPICard title="Active" value={kpis.active} />
                <KPICard title="Inactive" value={kpis.inactive} />
            </KPICardsGrid>

            <Table
                title="Staff Information"
                columns={columns}
                dataSource={filteredRows}
                searchValue={search}
                onSearchChange={(v) => setSearch(v)}
                showFilter={false}
                showMore={false}
                pagination
                pageSize={currentPageSize}
                serverPagination
                currentPage={currentPage}
                totalItems={search ? filteredRows.length : total}
                onPageChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== currentPageSize) {
                        setCurrentPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                $topMargin
                loading={loading}
                showDownload={showDownload}
                onExportExcel={onExportExcel}
                onExportPdf={onExportPdf}
                onRowClick={openEdit}
            />

            <AppModal
                open={open}
                onCancel={closeAndReset}
                title={mode === 'create' ? 'Register Staff Member' : 'Edit Staff Member'}
                className="staff-register-modal"
                width="86vw"
                style={{ maxWidth: 960 }}
                footer={
                    <Space>
                        <Button onClick={closeAndReset}>Cancel</Button>
                        <Button type="primary" loading={mode === 'create' ? createStaffMutation.isPending : updateStaffMutation.isPending} onClick={handleSubmit}>
                            {mode === 'create' ? 'Save Staff' : 'Save Changes'}
                        </Button>
                    </Space>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        authorities: {
                            access: {
                                students: 'no_access',
                                teachers: 'no_access',
                                staff: 'no_access',
                                tenantProfile: 'no_access',
                                classes: 'no_access',
                                schoolTime: 'no_access',
                                preferences: 'no_access',
                                studentAttendance: 'no_access',
                                teacherAttendance: 'no_access',
                                staffAttendance: 'no_access',
                                financeAccounts: 'no_access',
                                receiveLedger: 'no_access',
                                expenseLedger: 'no_access',
                                transactionLedger: 'view',
                                feesManagement: 'no_access',
                                teacherReport: 'no_access',
                                studentReport: 'no_access',
                                staffReport: 'no_access',
                            },
                        },
                    }}
                >
                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Users size={18} />
                            </IconWrap>
                            <SectionTitle>{mode === 'create' ? 'Create Staff' : 'Edit Staff'}</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="staffId" label="Staff ID">
                                    <AppInput placeholder="Optional" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="firstName" label="First Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="lastName" label="Last Name">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="email" label="Email" rules={mode === 'create' ? [{ required: true, type: 'email' }] : []}>
                                    <AppInput disabled={mode !== 'create'} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="phone" label="Phone">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="dateOfBirth" label="Date of Birth">
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="salary" label="Salary" rules={[{ required: true, message: 'Salary is required' }]}>
                                    <AppInput type="number" min={0} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'department']} label="Department">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'designation']} label="Designation">
                                    <AppInput />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['currentPosition', 'joiningDate']} label="Joining Date">
                                    <StyledDatePicker placeholder={dateFormat} format={dateInputFormats} />
                                </Form.Item>
                            </Col>
                            {mode === 'create' ? (
                                <>
                                    <Col xs={24} md={12} lg={8}>
                                        <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                                            <AppPasswordInput />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12} lg={8}>
                                        <Form.Item name="confirmPassword" label="Confirm Password" rules={[{ required: true, min: 6 }]}>
                                            <AppPasswordInput />
                                        </Form.Item>
                                    </Col>
                                </>
                            ) : null}
                            <Col xs={24}>
                                <Form.Item label="Profile Image">
                                    <ProfileImageRow>
                                        <ProfileImagePreview>
                                            {profileImagePreview ? (
                                                <img src={profileImagePreview} alt="Profile preview" />
                                            ) : (
                                                <UserOutlined style={{ fontSize: 26, opacity: 0.45 }} />
                                            )}
                                        </ProfileImagePreview>
                                        <div>
                                            <Upload
                                                accept="image/*"
                                                maxCount={1}
                                                showUploadList={false}
                                                beforeUpload={async (file) => {
                                                    if (!file.type?.startsWith?.('image/')) {
                                                        message.error('Please select an image file');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        message.error('Please select an image smaller than 5MB');
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    const dataUrl = await toDataUrl(file);
                                                    setProfileImagePreview(dataUrl);
                                                    return false;
                                                }}
                                            >
                                                <Button $primary={false} icon={<UploadOutlined />}>
                                                    Upload Image
                                                </Button>
                                            </Upload>
                                            <UploadHelp>JPG/PNG (Max 5MB)</UploadHelp>
                                        </div>
                                    </ProfileImageRow>
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>

                    <SectionDivider />

                    <FormSectionCard>
                        <CardHeader>
                            <IconWrap>
                                <Users size={18} />
                            </IconWrap>
                            <SectionTitle>Permissions</SectionTitle>
                        </CardHeader>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'dashboard']} label="Dashboard">
                                    <AppSelect
                                        options={[
                                            { value: 'admin', label: 'Admin' },
                                            { value: 'general', label: 'General' },
                                            { value: 'hr', label: 'HR' },
                                            { value: 'finance', label: 'Finance' },
                                        ]}
                                        placeholder="Select dashboard role"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'students']} label="Students">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'teachers']} label="Teachers">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'staff']} label="Staff">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'tenantProfile']} label="Tenant Profile">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'classes']} label="Class Management">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'schoolTime']} label="School Time">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'preferences']} label="Preferences">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'studentAttendance']} label="Student Attendance">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'teacherAttendance']} label="Teacher Attendance">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'staffAttendance']} label="Staff Attendance">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'financeAccounts']} label="Account Management">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'receiveLedger']} label="Receive Ledger">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'expenseLedger']} label="Expense Ledger">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'transactionLedger']} label="Transaction Ledger">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View only' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'feesManagement']} label="Fees Management">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'View' },
                                            { value: 'full', label: 'Full access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <div style={{ fontWeight: 600, marginTop: 12, marginBottom: 8, fontSize: 13, opacity: 0.8 }}>Report Access</div>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'teacherReport']} label="Teacher Report">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'Access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'studentReport']} label="Student Report">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'Access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name={['authorities', 'access', 'staffReport']} label="Staff Report">
                                    <AppSelect
                                        options={[
                                            { value: 'no_access', label: 'No access' },
                                            { value: 'view', label: 'Access' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormSectionCard>
                </Form>
            </AppModal>
        </DashboardContainer>
    );
}

